import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';
import Typography from '../Typography/Typography';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FormFieldProps {
  /** Field label — rendered as a `<label>` associated with `inputId`. */
  label?: string;
  /**
   * Error message. When provided:
   * - Activates error state styling on the control (via `hasError` on children)
   * - Replaces `helperText` in the sub-text slot
   * - Text is rendered in `text-error` colour
   */
  error?: string;
  /**
   * Helper text shown below the control when there is no `error`.
   * Rendered in `text-bodylight` colour.
   */
  helperText?: string;
  /**
   * The `id` of the control inside `children`. Used as the `htmlFor` on the
   * label so the association is always correct.
   */
  inputId: string;
  /** The form control — Input wrapper div, Select trigger, etc. */
  children: ReactNode;
  /** Additional class names on the outer column wrapper. */
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * **Internal** layout shell shared by all Primer form field components.
 *
 * Renders:
 * 1. An optional `<label>` above the control
 * 2. The control itself (`children`)
 * 3. A sub-text row below — always present in the DOM so the field height never
 *    jumps when transitioning between no-text → helper → error states.
 *    The row is `invisible` (height preserved) when there is nothing to show.
 *
 * This component is intentionally **not** exported from the Primer barrel. It
 * is an implementation detail of `Input`, `Select`, etc.
 */
export function FormField({
  label,
  error,
  helperText,
  inputId,
  children,
  className,
}: FormFieldProps) {
  const hasError = Boolean(error);
  const subText = error ?? helperText;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {/* ── Label ──────────────────────────────────────────────────────── */}
      {label && (
        <Typography variant="formLabel" size="default" as="label" htmlFor={inputId}>
          {label}
        </Typography>
      )}

      {/* ── Control (Input wrapper, Select trigger, …) ─────────────────── */}
      {children}

      {/* ── Sub-text row ────────────────────────────────────────────────
       * Always rendered so the row always occupies its line-height.
       * visibility:hidden (Tailwind: invisible) hides the text without
       * collapsing the space — prevents layout jump on error transition.
       */}
      <Typography
        variant="body"
        size="small"
        color={hasError ? 'error' : 'bodylight'}
        className={cn(!subText && 'invisible')}
      >
        {/* Non-breaking space ensures the line box is never zero-height
         * even when subText is undefined. */}
        {subText ?? '\u00a0'}
      </Typography>
    </div>
  );
}

export default FormField;
